import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, UserAvatar } from "@/components"
import { CommenterUser, DiscussionWithTop } from "@/models/Discussions"
import { TooltipComment } from "@/components/designSystem/Discussions/components/DiscussionChip/TooltipComment"
import { useDiscussions } from "@/services/store/useDiscussions"
import { useEffect, useMemo, useState } from "react"
import { RemainingDiscussionsChips } from "@/components/designSystem/Discussions/components/DiscussionChip/RemainingDiscussionsChips"

interface Props {
  discussions: DiscussionWithTop[]
  isExpanded: boolean
  showSelectedChip: boolean
  selectedDiscussionId: string
  setSelectedDiscussionId?: (id: string) => void
}

function removeDuplicateUsers(users: CommenterUser[]) {
  return Array.from(new Map(users.map((user) => [user.name, user])).values())
}

export function CombinedDiscussionsChips({
  isExpanded,
  showSelectedChip,
  discussions,
  selectedDiscussionId,
  setSelectedDiscussionId
}: Props) {
  const { setCurrentHoveredDiscussion, clearCurrentHoveredDiscussion, toggleExpandDiscussionsContainer } =
    useDiscussions()

  const [isRemainingButtonClicked, setIsRemainingButtonClicked] = useState(false)

  const { visibleDiscussions, remainingDiscussions, remainingCount } = useMemo(() => {
    if (!discussions || !discussions.length) {
      return { visibleDiscussions: [], remainingDiscussions: [], remainingCount: 0 }
    }

    // Show 3 avatars if total is <= 3, otherwise show 2 and a "+n" button
    const MAX_VISIBLE = discussions.length <= 3 ? 3 : 2
    const visible = discussions.slice(0, MAX_VISIBLE)
    const remaining = discussions.length > MAX_VISIBLE ? discussions.slice(MAX_VISIBLE) : []

    return {
      visibleDiscussions: visible,
      remainingDiscussions: remaining,
      remainingCount: remaining.length
    }
  }, [discussions])

  useEffect(() => {
    if (selectedDiscussionId) {
      const isSelectedInRemaining = remainingDiscussions.some((disc) => disc.id === selectedDiscussionId)

      if (isSelectedInRemaining) {
        setIsRemainingButtonClicked(true)
      } else if (visibleDiscussions.some((disc) => disc.id === selectedDiscussionId)) {
        setIsRemainingButtonClicked(false)
      }
    }
  }, [selectedDiscussionId, remainingDiscussions, visibleDiscussions])

  const handleClick = (discussionId: string, isRemaining: boolean) => {
    if (setSelectedDiscussionId) {
      setSelectedDiscussionId(discussionId)
    }

    setIsRemainingButtonClicked(isRemaining)

    if (!isExpanded) {
      toggleExpandDiscussionsContainer()
    }
  }

  return (
    <div className="flex -space-x-3 overflow-hidden">
      {visibleDiscussions.map((discussion: DiscussionWithTop) => {
        const discussionId = discussion.id
        const { id: authorId, avatar_url, display_name } = discussion.author_user || {}
        const firstComment = discussion.comments[0]
        const lastComment = discussion.comments[discussion.comments.length - 1]
        const isSelected = showSelectedChip && discussionId === selectedDiscussionId

        const commonProps = {
          "data-discussion-number": discussionId
        }

        const commentedUsers: CommenterUser[] = removeDuplicateUsers(
          discussion.comments.map((comment) => ({
            name: comment.author_user?.display_name ?? comment.author,
            imageSrc: comment.author_user?.avatar_url
          }))
        )

        return (
          <TooltipProvider key={discussionId}>
            <Tooltip>
              <TooltipTrigger
                className="z-10"
                onMouseEnter={() => setCurrentHoveredDiscussion(discussion)}
                onMouseLeave={() => clearCurrentHoveredDiscussion()}
                onClick={() => handleClick(discussionId, false)}
              >
                <div
                  {...commonProps}
                  className={`flex size-8 items-center justify-center rounded-full ${
                    isSelected ? "bg-tint" : "bg-background"
                  }`}
                >
                  <div
                    {...commonProps}
                    className={`z-0 flex size-7 items-center justify-center rounded-full ${
                      discussion.discussion_state === "resolved" ? "bg-success-dark" : "bg-highlight-normal"
                    } p-0.5`}
                  >
                    <div
                      {...commonProps}
                      className="flex size-6 items-center justify-center rounded-full bg-background p-0.5"
                    >
                      <UserAvatar
                        key={authorId}
                        username={display_name || discussion.author}
                        avatarURL={avatar_url}
                        className="size-full"
                      />
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              {!isExpanded && (
                <TooltipContent className="absolute left-[-420px] top-0 min-h-[80px] w-[420px] rounded-xl bg-background shadow-md">
                  <TooltipComment
                    authorComment={firstComment}
                    commentedUsers={commentedUsers.slice(1)}
                    isResolved={discussion.discussion_state === "resolved"}
                    lastComment={lastComment}
                  />
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )
      })}

      {remainingCount > 0 && (
        <RemainingDiscussionsChips
          isExpanded={isExpanded}
          remainingDiscussions={remainingDiscussions}
          remainingCount={remainingCount}
          handleClick={handleClick}
          isRemainingButtonClicked={isRemainingButtonClicked}
          selectedDiscussionId={selectedDiscussionId}
        />
      )}
    </div>
  )
}
